import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useActingCustomerId } from '@/lib/actingContext';
import type { Customer, CustomerLocation, CustomerUser } from '@/types/domain';

interface CustomerContext {
  user: CustomerUser;
  customer: Customer;
  locations: CustomerLocation[];
}

/**
 * Resolve the currently signed-in user's customer + locations.
 *
 * RLS in the `orders` schema isolates rows to the authenticated user's
 * customer_id (via `orders.current_customer_id()`), so an unfiltered
 * select returns exactly the rows this user is allowed to see.
 *
 * Returns null when there is no signed-in user, or when the user has not
 * yet been linked to a customer (which is true for fresh accounts until
 * an admin inserts an `orders.customer_users` row — see CLAUDE.md auth flow).
 */
export function useCustomer() {
  // Per-tab acting context (docs/ACTING-CONTEXT.md). When set it wins over the
  // persisted column; it's in the query key so switching customers in a tab
  // refetches everything under the new context. RLS validates the id, so a
  // bogus tab value can't reach another customer's data.
  const actingId = useActingCustomerId();

  return useQuery<CustomerContext | null>({
    queryKey: ['customer-context', actingId],
    queryFn: async (): Promise<CustomerContext | null> => {
      const { data: authData } = await supabase.auth.getUser();
      const authUser = authData.user;
      if (!authUser) return null;

      const { data: userRow, error: userErr } = await supabase
        .schema('orders')
        .from('customer_users')
        .select(
          'id, customer_id, active_customer_id, full_name, role, default_location_id, phone',
        )
        .eq('id', authUser.id)
        .maybeSingle();

      if (userErr) throw userErr;
      if (!userRow) return null;

      // Resolution order: this tab's acting context → the persisted
      // active_customer_id → legacy customer_id. Mirrors what the DB's
      // current_customer_id() resolver does, so the app view and RLS agree.
      const activeCustomerId =
        actingId ?? userRow.active_customer_id ?? userRow.customer_id;
      if (!activeCustomerId) return null;

      const [customerRes, locationsRes] = await Promise.all([
        supabase
          .schema('orders')
          .from('customers')
          .select(
            'id, qbo_customer_id, name, account_number, payment_terms, credit_limit, on_credit_hold, active, features, delivery_schedule',
          )
          .eq('id', activeCustomerId)
          .single(),
        supabase
          .schema('orders')
          .from('customer_locations')
          .select(
            'id, customer_id, location_code, display_name, address_line1, address_line2, city, state, zip, delivery_notes, active, delivery_schedule',
          )
          .eq('customer_id', activeCustomerId)
          .eq('active', true)
          .order('display_name'),
      ]);

      if (customerRes.error) throw customerRes.error;
      if (locationsRes.error) throw locationsRes.error;

      const user: CustomerUser = {
        id: userRow.id,
        customerId: activeCustomerId,
        fullName: userRow.full_name,
        role: userRow.role,
        defaultLocationId: userRow.default_location_id,
        phone: userRow.phone,
      };

      const c = customerRes.data;
      const customer: Customer = {
        id: c.id,
        qboCustomerId: c.qbo_customer_id,
        name: c.name,
        accountNumber: c.account_number,
        paymentTerms: c.payment_terms,
        creditLimit: c.credit_limit,
        onCreditHold: c.on_credit_hold,
        active: c.active,
        cardOnFileExempt:
          (c.features as Record<string, boolean> | null)?.card_on_file_exempt === true,
        deliverySchedule: c.delivery_schedule,
      };

      const locations: CustomerLocation[] = (locationsRes.data ?? []).map((row) => ({
        id: row.id,
        customerId: row.customer_id,
        locationCode: row.location_code,
        displayName: row.display_name,
        addressLine1: row.address_line1,
        addressLine2: row.address_line2,
        city: row.city,
        state: row.state,
        zip: row.zip,
        deliveryNotes: row.delivery_notes,
        active: row.active,
        deliverySchedule: row.delivery_schedule,
      }));

      // The account's OWN address leads, then the rest alphabetically.
      //
      // ⚠ A group master can still hold a location per store — SCOLARIS'
      // three stores have no accounts of their own, so those rows are how
      // they are reachable. Plain alphabetical order therefore let whichever
      // store sorted first stand in as corporate's delivery address: THE MELT
      // MAIN's account page read "MELT-SACRAMENTO". `PRIMARY` is the code
      // every importer has ever written for "this account's own address", and
      // it does not sort first on its own (it lands between EQUIPMENT and
      // SACRAMENTO), so the ordering has to be explicit.
      locations.sort((a, b) => {
        const ap = a.locationCode === 'PRIMARY' ? 0 : 1;
        const bp = b.locationCode === 'PRIMARY' ? 0 : 1;
        if (ap !== bp) return ap - bp;
        return (a.displayName ?? '').localeCompare(b.displayName ?? '');
      });

      return { user, customer, locations };
    },
    staleTime: 5 * 60 * 1000,
  });
}
